import {
  PAIR_VARIANT_SUFFIX,
  isPlaygroundPair,
  listPlaygroundTests,
  matchPlaygroundPaths,
  playgroundEntries,
  playgroundNameOfFile,
  resolvePairVariant,
  type PlaygroundConfig,
  type PlaygroundDefinition,
  type PlaygroundMode,
  type PlaygroundTestListing,
} from '@gotti/playground/browser';
import { ProjectLoader, loadPlaygroundConfig, normalizeProjectPath } from '@gotti/sandbox-core';

/** One playground of a project, as its config discovers it, with its tests listed — nothing booted. */
export interface ProjectPlayground {
  /** As `gotti playground` names it: the file's name, an inline entry's key, a pair's halves `<name>` and `<name>-offline`. */
  name: string;
  /** The tree path of the file that declares it: the definition file, or the config for an inline entry. */
  file: string;
  /** An inline entry's key in the config's `playgrounds`. */
  member?: string;
  /** A pair's half. */
  variant?: PlaygroundMode;
  tests: PlaygroundTestListing[];
}

/** Every playground a project's config discovers, and what failed to load on the way. */
export interface ProjectPlaygroundListing {
  playgrounds: ProjectPlayground[];
  /** What failed to load, by the tree path it failed in — the config, or one definition file — with the loader's message. */
  errors: { file: string; message: string }[];
}

export const PLAYGROUND_CONFIG_PATH = 'playground.config.ts';
const PAIR_VARIANTS: readonly PlaygroundMode[] = ['online', 'offline'];

const messageOf = (err: unknown): string => (err instanceof Error ? err.message : String(err));

/** A module's default export, a function form called as `gotti playground` calls it, then unwrapped again. */
const definitionValue = async (module: unknown): Promise<unknown> => {
  const unwrap = (value: unknown) => (value && typeof value === 'object' && 'default' in value ? (value as { default: unknown }).default : value);
  let value = unwrap(module);
  if (typeof value === 'function') value = unwrap(await (value as (env: object) => unknown)({ command: 'serve', mode: 'development' }));
  return value;
};

/**
 * Every playground `playground.config.ts` discovers in the project tree, by the rules `gotti playground` applies (the
 * browser entry's `playgroundEntries`, `matchPlaygroundPaths`, `playgroundNameOfFile`, `resolvePairVariant` and
 * `listPlaygroundTests`), each with its tests listed — evaluated through one project loader, nothing booted. A file a
 * pattern names is named after itself, an inline entry after its key or `name`, a pair is its two halves. Unlike the
 * CLI, one bad entry never hides the rest: a definition that fails to load, a pattern that names no file and a second
 * playground of a name are each reported in `errors` (the first of a name is kept), and the rest are listed. A config
 * that fails to load lists nothing. A config's `definitionPreload` is not run — it is a hook of the CLI's node loader —
 * so a definition that needs one shows as a load error.
 */
export async function listProjectPlaygrounds(
  files: Record<string, string>,
  externalModules: ReadonlyMap<string, unknown>,
): Promise<ProjectPlaygroundListing> {
  const loader = new ProjectLoader(files, { externalModules, console });
  const errors: ProjectPlaygroundListing['errors'] = [];
  let config: PlaygroundConfig;
  try {
    config = (await loadPlaygroundConfig(loader, PLAYGROUND_CONFIG_PATH)) as unknown as PlaygroundConfig;
  } catch (err) {
    return { playgrounds: [], errors: [{ file: PLAYGROUND_CONFIG_PATH, message: messageOf(err) }] };
  }

  const playgrounds: ProjectPlayground[] = [];
  const add = (found: ProjectPlayground[], file: string) => {
    for (const playground of found) {
      const taken = playgrounds.find((existing) => existing.name === playground.name);
      if (taken) {
        errors.push({ file, message: `Duplicate playground name found: "${playground.name}" (${taken.file} and ${playground.file})` });
        continue;
      }
      playgrounds.push(playground);
    }
  };
  /** A definition (or a pair's halves) as the playgrounds it is, its tests listed. */
  const playgroundsOf = async (value: unknown, name: string, file: string, member?: string): Promise<ProjectPlayground[]> => {
    const at = member === undefined ? {} : { member };
    if (isPlaygroundPair(value)) {
      return Promise.all(PAIR_VARIANTS.map(async (variant) => {
        const halfName = `${name}${PAIR_VARIANT_SUFFIX[variant]}`;
        const definition = await resolvePairVariant(value, variant, { config });
        return { name: halfName, file, ...at, variant, tests: listPlaygroundTests(definition, halfName) };
      }));
    }
    return [{ name, file, ...at, tests: listPlaygroundTests(value as PlaygroundDefinition<any, any, any>, name) }];
  };

  const paths = Object.keys(files).map((path) => normalizeProjectPath(path)).filter((path) => path !== PLAYGROUND_CONFIG_PATH);
  for (const declared of playgroundEntries(config)) {
    if ('name' in declared) {
      try {
        if (!isPlaygroundPair(declared.entry) && (!declared.entry || typeof declared.entry !== 'object')) {
          throw new Error(`Playground "${declared.name}" in ${PLAYGROUND_CONFIG_PATH} is not a definition object (or a pair).`);
        }
        add(await playgroundsOf(declared.entry, declared.name, PLAYGROUND_CONFIG_PATH, declared.name), PLAYGROUND_CONFIG_PATH);
      } catch (err) {
        errors.push({ file: PLAYGROUND_CONFIG_PATH, message: messageOf(err) });
      }
      continue;
    }
    const matches = matchPlaygroundPaths(declared.pattern, paths);
    if (!matches.length) {
      errors.push({ file: PLAYGROUND_CONFIG_PATH, message: `No playground files found matching pattern: "${declared.pattern}"` });
      continue;
    }
    for (const file of matches) {
      try {
        const value = await definitionValue(loader.load(file));
        if (value && typeof value === 'object' && 'playgrounds' in value) {
          throw new Error(`Playground definition file ${file} is a playground config (it declares \`playgrounds\`): nested configs are not supported; list its definition files.`);
        }
        if (!isPlaygroundPair(value) && (!value || typeof value !== 'object')) {
          throw new Error(`Playground definition file ${file} did not export a definition object.`);
        }
        add(await playgroundsOf(value, playgroundNameOfFile(file), file), file);
      } catch (err) {
        errors.push({ file, message: messageOf(err) });
      }
    }
  }
  return { playgrounds, errors };
}
